import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Desktop, Mobile } from '../../App';
import { useAppSelector } from '../../app/hooks';
import { AnimatedDashboard } from '../../components/animation/AnimatedDashboard';
import { AnimatedItem } from '../../components/animation/AnimatedItem';
import { SidebarModal } from '../../components/common/Sidebar/Sidebar';
import Spinner from '../../components/common/Spinner';
import {
  CLUB,
  LP,
  MIX,
  USDC,
  USDT,
  formatUSD,
  formatUSDNoDecimals
} from '../../helpers/helperFunctions';
import { getOrders } from '../../slices/orders-slice';
import { Flexed, PageTitle } from '../../styles/GlobalComponents';
import { FlexBetween, HubLengthMessage } from '../../styles/stylesStyled';
import { CurrencyType } from '../../types/types';
import {
  InvoiceHeader,
  Invoices,
  InvoicesMobile
} from '../Accounting/AccountingStyled';
import {
  MyOrderListModel,
  PurchaseSteps,
  SubsctriptionTypes
} from '../Marketplace/Maeketplace.types';
import CancelPaymentPackage from './CancelPaymentPackage';
import WaitingForPaymentPackage from './WaitingForPaymentPackage';
import { TextWithTooltip } from '../../components/common/TextWithTooltip';
import { CaptionText } from '../../styles/FontStyles';
import { colors, lightTheme } from '../../styles/GlobalStyles';
import PaymentPage from '../Marketplace/PurchasePackageAndCombo/PaymentPage';
import { AnimatedTab } from '../../components/animation/AnimatedTab';

import {
  getSaveAndTravelInit,
  setOrderId,
  setPurchaseCardStep
} from '../Marketplace/Marketplace.actions';
import { Success } from './Success';
import { Failed } from './Failed';
import PaymentSaveAndTravel from '../Marketplace/PurchasePackageAndCombo/PaymentSaveAndTravel';

export enum OrderType {
  ORDER = 1,
  PACKAGE,
  LICENCE,
  COMBO,
  DISCOUNT_CODE,
  TRAVEL_SUBSCRIPTION,
  SUBCRIPTION_BUNDLE,
  GOLDEN_CARD = 9,
  SUPER_SALES = 10,
  TRAVELUTION_NO_RENEWAL_PASS = 13,
  EVENT_TICKET = 14,
  SAVE_AND_TRAVEL = 15,
  SUPER_SALES_SUBCRIPTION = 16,
  ACCESS_PACKS = 17
}

export const Orders = () => {
  const [isSideModalOpen, setIsSideModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<MyOrderListModel | null>(null);
  const { t } = useTranslation('packages');
  const dispatch = useDispatch();

  const { loading, orders } = useAppSelector((state) => state.orders);
  const { step } = useAppSelector((state) => state.marketplace);

  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  const orderStatus = (inv: MyOrderListModel) => {
    switch (inv.status) {
      case 1:
        return (
          <>
            <div style={{ marginTop: 4, color: colors.primary900 }}>
              <b
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (
                    inv.type === SubsctriptionTypes.SAVE_AND_TRAVEL ||
                    inv.type === SubsctriptionTypes.SUPER_SALES_SUBCRIPTION
                  ) {
                    dispatch(
                      getSaveAndTravelInit({
                        orderId: inv.orderId
                      })
                    );
                    dispatch(
                      setPurchaseCardStep({
                        step: PurchaseSteps.PAYMENT_SAVE_AND_TRAVEL
                      })
                    );
                  } else {
                    dispatch(
                      setPurchaseCardStep({ step: PurchaseSteps.PAYMENT_PAGE })
                    );
                  }
                  setIsSideModalOpen(!isSideModalOpen);
                }}>
                Continue with payment
              </b>
            </div>

            {inv.type !== OrderType.GOLDEN_CARD &&
              inv.type !== OrderType.EVENT_TICKET &&
              inv.type !== OrderType.SAVE_AND_TRAVEL &&
              inv.type !== OrderType.SUPER_SALES_SUBCRIPTION && (
                <div style={{ marginTop: 4 }}>
                  <span>
                    <b
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setIsCancelModalOpen(!isCancelModalOpen);
                      }}>
                      Cancel payment
                    </b>
                  </span>
                </div>
              )}
          </>
        );

      case 2:
        return <> {t('my_hubs_part.paid')}</>;
      case 3:
        return <>{t('my_hubs_part.canceled')}</>;
      case 4:
        return <>{t('my_hubs_part.canceled')}</>;
      case 5:
        return <>Past due</>;
      default:
    }
  };

  const currencyTypeRender = (currencyType: CurrencyType) => {
    switch (currencyType) {
      case CurrencyType.BANK_TRANSFER:
        return `${USDT}`;
      case CurrencyType.SHOPPING_POINTS:
        return CLUB;
      case CurrencyType.REWARD_POINTS:
        return USDT;
      case CurrencyType.LOYALTY_POINTS:
        return LP;
      case CurrencyType.USDC:
        return `${USDC}`;
      case CurrencyType.MIX:
        return 'MULTIPLE';
    }
  };

  if (loading && !orders) {
    return <Spinner fullScreen />;
  }

  return (
    <AnimatedDashboard zoom>
      <PageTitle> {t('orders')}</PageTitle>
      <Desktop>
        <InvoiceHeader>
          <div style={{ width: '16.66%' }}>
            {t('my_hubs_part.date_created')}
          </div>
          <div style={{ width: '16.66%' }}>{t('my_hubs_part.date_paid')}</div>
          <div style={{ width: '16.66%' }}>{t('my_hubs_part.hub_name')}</div>
          <div style={{ width: '16.66%' }}>{t('my_hubs_part.order_id')}</div>
          <div style={{ width: '16.66%' }}>{t('my_hubs_part.value')}</div>
          <div
            style={{
              width: '16.66%',
              textAlign: 'right'
            }}>
            {t('my_hubs_part.status')}
          </div>
        </InvoiceHeader>
      </Desktop>
      {orders?.length === 0 ? (
        <HubLengthMessage
          // onClick={() => dispatch(setSelectedTab('hub'))}
          style={{ cursor: 'pointer' }}>
          {t('my_hubs_part.no_hubs')}
        </HubLengthMessage>
      ) : (
        orders?.map((inv: MyOrderListModel, i: number) => {
          return (
            <AnimatedItem key={i} index={i}>
              <Desktop>
                <Invoices>
                  <div style={{ width: '16.66%' }}>
                    {moment.utc(inv?.dateCreated).format('DD/MM/YY')}
                  </div>
                  <div style={{ width: '16.66%' }}>
                    {inv?.datePaid &&
                      moment.utc(inv?.datePaid).format('DD/MM/YY')}
                  </div>
                  <div style={{ width: '16.66%' }}>
                    {!inv?.name ? 'Free Trial Backoffice' : inv?.name}
                  </div>
                  <div style={{ width: '16.66%' }}>#{inv.orderId}</div>
                  <Flexed alignCenter style={{ width: '16.66%' }}>
                    <b>
                      <span>
                        {CurrencyType.MIX !== inv.currencyType &&
                          formatUSDNoDecimals(inv.price)}{' '}
                        EUR
                      </span>
                    </b>
                    {CurrencyType.MIX === inv.currencyType && (
                      <TextWithTooltip
                        margin='0 0 0 10px'
                        width='auto'
                        tooltip={
                          <div>
                            {inv.payments.map((payment, i) => (
                              <CaptionText
                                color={lightTheme.colors.neutral500}
                                key={i}>
                                <span
                                  style={{
                                    width: 150,
                                    display: 'inline-block'
                                  }}>
                                  {payment.amount}{' '}
                                  {currencyTypeRender(payment.currencyType)}{' '}
                                </span>
                                {moment(payment.dateCreated).format(
                                  'YYYY-MM-DD HH:mm:ss'
                                )}
                              </CaptionText>
                            ))}
                            {inv.amountToPay > 0 && (
                              <CaptionText
                                color={lightTheme.colors.neutral500}
                                key={i}>
                                <span
                                  style={{
                                    width: 150,
                                    display: 'inline-block'
                                  }}>
                                  {formatUSD(inv.amountToPay)}{' '}
                                  {inv.currencyAmountToPay}
                                </span>
                                <span
                                  style={{ color: lightTheme.colors.warning }}>
                                  PENDING
                                </span>
                              </CaptionText>
                            )}
                          </div>
                        }></TextWithTooltip>
                    )}
                  </Flexed>
                  <div
                    style={{
                      width: '16.66%',
                      textAlign: 'right'
                    }}
                    onClick={() => setSelectedPackage(inv)}>
                    {orderStatus(inv)}
                  </div>
                </Invoices>
              </Desktop>
              <Mobile>
                <InvoicesMobile style={{ cursor: 'pointer' }}>
                  <FlexBetween>
                    <div
                      style={{
                        fontWeight: 500,
                        lineHeight: '20px'
                      }}>
                      {!inv?.name ? (
                        'Free Trial Backoffice'
                      ) : (
                        <>
                          {inv.name.includes('+') ? (
                            <>
                              {inv.name.substring(0, inv.name.indexOf('+') - 1)}
                              <br />
                              {inv.name.substring(inv.name.indexOf('+') - 1)}
                            </>
                          ) : (
                            inv.name
                          )}
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        fontWeight: 500,
                        lineHeight: '20px',
                        fontSize: 18
                      }}>
                      <Flexed alignCenter>
                        <b>
                          <span>
                            {CurrencyType.MIX !== inv.currencyType &&
                              formatUSDNoDecimals(inv.price)}{' '}
                            {currencyTypeRender(inv.currencyType)}
                          </span>
                        </b>
                        {CurrencyType.MIX === inv.currencyType && (
                          <TextWithTooltip
                            margin='0 0 0 10px'
                            width='auto'
                            tooltip={
                              <div>
                                {inv.payments.map((payment, i) => (
                                  <CaptionText
                                    color={lightTheme.colors.neutral500}
                                    key={i}>
                                    <span
                                      style={{
                                        width: 150,
                                        display: 'inline-block'
                                      }}>
                                      {payment.amount}{' '}
                                      {currencyTypeRender(payment.currencyType)}{' '}
                                    </span>
                                    {moment(payment.dateCreated).format(
                                      'YYYY-MM-DD HH:mm:ss'
                                    )}
                                  </CaptionText>
                                ))}
                                {inv.amountToPay > 0 && (
                                  <CaptionText
                                    color={lightTheme.colors.neutral500}
                                    key={i}>
                                    <span
                                      style={{
                                        width: 150,
                                        display: 'inline-block'
                                      }}>
                                      {formatUSD(inv.amountToPay)} EUR{' '}
                                    </span>
                                    <span
                                      style={{
                                        color: lightTheme.colors.warning
                                      }}>
                                      PENDING
                                    </span>
                                  </CaptionText>
                                )}
                              </div>
                            }></TextWithTooltip>
                        )}
                      </Flexed>
                    </div>
                  </FlexBetween>
                  <FlexBetween>
                    <div>
                      {moment.utc(inv?.dateCreated).format('DD/MM/YY')}{' '}
                      <b style={{ marginLeft: '5px' }}>
                        <span>
                          {t('my_hubs_part.order_id')} {inv?.orderId}
                        </span>
                      </b>
                    </div>
                    <div
                      style={{
                        lineHeight: '18px',
                        fontSize: 12
                      }}
                      onClick={() => setSelectedPackage(inv)}>
                      {orderStatus(inv)}
                    </div>
                  </FlexBetween>
                </InvoicesMobile>
              </Mobile>
            </AnimatedItem>
          );
        })
      )}

      <SidebarModal
        isOpen={isSideModalOpen}
        setOpen={setIsSideModalOpen}
        title={t('my_hubs_part.payment_instructions_title')}
        sidebarWidth={'511px'}>
        {step === PurchaseSteps.PAYMENT_PAGE && (
          <AnimatedTab key={PurchaseSteps.PAYMENT_PAGE}>
            <PaymentPage selectedPackage={selectedPackage} />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.PAYMENT_SAVE_AND_TRAVEL && (
          <AnimatedTab key={PurchaseSteps.PAYMENT_SAVE_AND_TRAVEL}>
            <PaymentSaveAndTravel selectedPackage={selectedPackage} />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.SUCCESS && (
          <AnimatedTab key={PurchaseSteps.SUCCESS}>
            <Success setIsSideModalOpen={setIsSideModalOpen} />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.FAILED && (
          <AnimatedTab key={PurchaseSteps.FAILED}>
            <Failed setIsSideModalOpen={setIsSideModalOpen} />
          </AnimatedTab>
        )}
      </SidebarModal>

      <SidebarModal
        isOpen={isCancelModalOpen}
        setOpen={setIsCancelModalOpen}
        title={t('my_hubs_part.cancel_order_title')}
        sidebarWidth={'511px'}>
        <CancelPaymentPackage
          setIsCancelModalOpen={setIsCancelModalOpen}
          selectedPackage={selectedPackage}
        />
      </SidebarModal>
    </AnimatedDashboard>
  );
};
